import {use, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Comments} from '../comments/Comments'
import {CommentsContext} from '../comments/CommentsContext'
import {CommentsViewControls} from '../comments/CommentsViewControls'
import type {IssueCommentComposerHandle} from '../comments/IssueCommentComposer'
import {useFilteredCommentsData} from '../comments/use-filtered-comments-data'
import {useLayoutAuthorLogin, usePullRequestBasePath} from '../../stores/layout-selectors'
import styles from './CommentsSection.module.css'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useCommentsFilterState} from '../comments/use-comments-filter-state'
import {Blankslate} from '@primer/react/experimental'
import {AlertIcon, CommentDiscussionIcon} from '@primer/octicons-react'
import {Banner, Link, VisuallyHidden} from '@primer/react'
import {useViewModeFromSearchParams} from './data/use-comments-routing'
import {useCommentsSortState} from '../comments/use-comments-sort-state'
import {OVERVIEW_COMMENTS_ANCHOR_ID} from '../../helpers/constants'
import {PullRequestCommentStateAction} from './PullRequestCommentStateAction'
import {displayModeForViewMode} from '../comments/utils/display-mode'
import {useLocation} from '@github-ui/react-router'
import {useInfiniteConversationCommentsQuery} from '../../page-data/loaders/use-conversation-comments'
import {FocusHashTargetGuardContext, useFocusHashTarget} from '../../hooks/use-focus-hash-target'
import {announce} from '@github-ui/aria-live'

const unavailableCommentMessage =
  'This comment could not be found. It may have been deleted or is no longer visible to you.'

interface CommentsSectionProps {
  skipTargetId?: string
}

interface CommentsHeadingProps extends CommentsSectionProps {
  anchorId?: string
}

export function CommentsHeading({anchorId, skipTargetId}: CommentsHeadingProps) {
  return (
    <div className={styles.headingArea}>
      <CommentDiscussionIcon size={24} className={styles.headingIcon} aria-hidden="true" />
      <div className={styles.headingWithSkipLink}>
        <h2 id={anchorId} className={styles.heading} tabIndex={anchorId ? -1 : undefined}>
          Comments
        </h2>
        {skipTargetId && (
          <VisuallyHidden className={styles.skipLinkContainer}>
            <Link href={`#${skipTargetId}`} className={styles.skipLink}>
              Skip comments
            </Link>
          </VisuallyHidden>
        )}
      </div>
      <div className={styles.headingDivider} />
    </div>
  )
}

function AnchoredCommentsHeading({skipTargetId}: CommentsSectionProps) {
  useFocusHashTarget(OVERVIEW_COMMENTS_ANCHOR_ID)

  return <CommentsHeading anchorId={OVERVIEW_COMMENTS_ANCHOR_ID} skipTargetId={skipTargetId} />
}

function ErrorFallback() {
  return (
    <Blankslate>
      <Blankslate.Visual>
        <AlertIcon size="medium" />
      </Blankslate.Visual>
      <Blankslate.Heading>Failed to load comments</Blankslate.Heading>
      <Blankslate.Description>
        Please{' '}
        <Link inline href="#" onClick={() => window.location.reload()}>
          refresh the page
        </Link>{' '}
        to try again.
      </Blankslate.Description>
    </Blankslate>
  )
}
export function CommentsSection({skipTargetId}: CommentsSectionProps) {
  const [viewMode, setViewMode] = useViewModeFromSearchParams()

  const commentsContextValue = useMemo(() => ({viewMode, setViewMode}), [viewMode, setViewMode])

  return (
    <section className={styles.container}>
      <ErrorBoundary
        fallback={
          <>
            <AnchoredCommentsHeading skipTargetId={skipTargetId} />
            <ErrorFallback />
          </>
        }
        boundaryName="OverviewCommentsSection"
      >
        <CommentsContext value={commentsContextValue}>
          <CommentsSectionContent skipTargetId={skipTargetId} />
        </CommentsContext>
      </ErrorBoundary>
    </section>
  )
}

function CommentsSectionContent({skipTargetId}: CommentsSectionProps) {
  const pullRequestAuthorLogin = useLayoutAuthorLogin()
  const basePath = usePullRequestBasePath()
  const {hash, key: locationKey} = useLocation()
  const targetCommentId = /^#issuecomment-(\d+)$/.exec(hash)?.[1]

  const viewMode = use(CommentsContext).viewMode
  const displayMode = displayModeForViewMode(viewMode)
  const {filterState, setCurrentFilterState, resetFilterStates} = useCommentsFilterState(viewMode)
  const [previousLocationKey, setPreviousLocationKey] = useState(locationKey)
  if (previousLocationKey !== locationKey) {
    setPreviousLocationKey(locationKey)
    if (targetCommentId) resetFilterStates()
  }
  const [sort, setSort] = useCommentsSortState(displayMode)
  const {
    authorList,
    filteredConversationItems,
    totalConversationItemsCount,
    allReviewThreadsResolved,
    hasCurrentUserReviewThreads,
  } = useFilteredCommentsData({displayMode, filterState, sort})

  const composerRef = useRef<IssueCommentComposerHandle>(null)
  const handleQuoteReply = useCallback(
    (quotedText: string) => composerRef.current?.insertQuote(quotedText, {append: true}),
    [],
  )
  const {data, hasNextPage, isFetching, isError, isSuccess, fetchNextPage} = useInfiniteConversationCommentsQuery({
    basePath,
    enabled: displayMode === 'conversation',
  })
  const targetLoaded = data?.comments.some(
    comment => comment.type === 'issue' && String(comment.id) === targetCommentId,
  )
  const needsTargetPage = !!targetCommentId && !targetLoaded && hasNextPage && isSuccess
  const targetUnavailable = !!targetCommentId && !targetLoaded && isSuccess && !hasNextPage
  const pendingFocus = useRef<{key: string; moved: boolean} | null>(null)
  const shouldFocusTarget = useCallback(
    () => pendingFocus.current?.key !== locationKey || !pendingFocus.current.moved,
    [locationKey],
  )

  useEffect(() => {
    if (!needsTargetPage) return

    const focusedAtNavigation = document.activeElement
    pendingFocus.current = {key: locationKey, moved: false}
    const onFocus = () => {
      if (document.activeElement !== focusedAtNavigation) {
        if (pendingFocus.current?.key === locationKey) pendingFocus.current.moved = true
      }
    }
    document.addEventListener('focusin', onFocus)
    return () => document.removeEventListener('focusin', onFocus)
  }, [needsTargetPage, locationKey])

  useEffect(() => {
    if (needsTargetPage && !isFetching && !isError) void fetchNextPage()
  }, [needsTargetPage, isFetching, isError, fetchNextPage])

  useEffect(() => {
    if (targetUnavailable) announce(unavailableCommentMessage)
  }, [targetUnavailable, locationKey])

  return (
    <>
      <div className={styles.controlsRow}>
        <AnchoredCommentsHeading skipTargetId={skipTargetId} />
        <CommentsViewControls
          authorList={authorList}
          filterState={filterState}
          setFilterState={setCurrentFilterState}
          sort={sort}
          setSort={setSort}
          viewMode={viewMode}
        />
      </div>
      {targetUnavailable && (
        <Banner variant="warning" title="Comment unavailable" description={unavailableCommentMessage} />
      )}
      <FocusHashTargetGuardContext value={shouldFocusTarget}>
        <Comments
          filteredConversationItems={filteredConversationItems}
          filterState={filterState}
          sort={sort}
          displayMode={displayMode}
          handleQuoteReply={handleQuoteReply}
          pullRequestAuthorLogin={pullRequestAuthorLogin}
          totalConversationItemsCount={totalConversationItemsCount}
          allReviewThreadsResolved={allReviewThreadsResolved}
          hasCurrentUserReviewThreads={hasCurrentUserReviewThreads}
          composerRef={composerRef}
          renderAdditionalComposerActions={context => <PullRequestCommentStateAction {...context} />}
          onSubmitted={resetFilterStates}
        />
      </FocusHashTargetGuardContext>
    </>
  )
}
